// Orchestrator Work Item — design mock for the per-part case detail view.
// Hardcoded mock data; not wired to any backend. Shows:
//  - Universal shell (header / trigger context / activity rail)
//  - Alternatives table with per-alt review state (cross-ref state lives here)
//  - Drill-into-alt side-panel for deep compare
//  - Per-BOM decisions section
//  - Buyer / Engineer role toggle to demonstrate dual visibility
//
// Companion to: EngineeringInboxMockPage.tsx
// Routes:
//   /engineering/inbox-mock          (the inbox)
//   /engineering/inbox-mock/detail   (this page)
import React, { useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Stack,
  Divider,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  alpha,
  useTheme,
  Tooltip,
  Drawer,
  TextField,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  CheckCircle as CheckIcon,
  Cancel as RejectIcon,
  Pending as PendingIcon,
  Info as InfoIcon,
  Add as AddIcon,
  AutoAwesome as AgentIcon,
  ChevronRight as ChevronRightIcon,
  FiberManualRecord as DotIcon,
  AttachFile as AttachIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';

// ── Types ────────────────────────────────────────────────────

type Role = 'BUYER' | 'ENGINEER';

type ReviewState =
  | 'DISCOVERED'
  | 'BUYER_REVIEWING'
  | 'BUYER_APPROVED'
  | 'BUYER_REJECTED'
  | 'ENG_REVIEWING'
  | 'ENG_APPROVED'
  | 'ENG_REJECTED'
  | 'CONDITIONAL';

interface Alternative {
  crossRefId: string;
  candidateMpn: string;
  candidateName: string;
  manufacturer: string;
  source: 'SYSTEM_AUTO' | 'MI_AGENT' | 'MFR_SUGGESTED' | 'BUYER_MANUAL' | 'ENG_MANUAL';
  reviewState: ReviewState;
  fffMatchScore: number;
  costDeltaPct: number;
  bestPriceUsd: number;
  bestSupplier: string;
  stockUnits: number;
  leadTimeWeeks: number;
  buyerRationale?: string;
  engRationale?: string;
  conditions?: string;
  reviewedAt?: string;
}

interface BomImpact {
  bomId: string;
  bomName: string;
  unitsPerQtr: number;
  stockWeeks: number;
  bestAvailableAlt?: string;
  decision?: 'SWAP' | 'LTB_STOCKPILE' | 'NEGOTIATE_LTB' | 'REDESIGN' | 'NO_OP';
  decisionAlt?: string;
}

// ── Mock data ────────────────────────────────────────────────

const PART = {
  partId: 'p-stm32f407',
  mpn: 'STM32F407VGT6',
  description: 'STMicroelectronics · MCU 32-bit ARM Cortex-M4 · LQFP100',
};

const PCN = {
  pcnId: 'pcn-ST-2026-0142',
  manufacturer: 'STMicroelectronics',
  reference: 'PCN-ST-2026-0142',
  changeType: 'EOL' as const,
  changeDescription: 'End of life — wafer fab transition',
  effectiveDate: '2026-08-10',
  ltbDate: '2026-08-12',
  lastShipDate: '2027-02-10',
  mfrSuggestedAlt: 'STM32F417VGT6',
  detectionSource: 'AGGREGATOR_API',
  detectionConfidence: 0.92,
};

const WI = {
  workItemId: 'wi-001',
  triggerType: 'PCN' as const,
  taskType: 'PCN_RESPONSE' as const,
  severity: 'CRITICAL' as const,
  status: 'IN_PROGRESS' as const,
  ownerRole: 'BUYER' as const,
  exposure: {
    revenueAtRisk: 4_200_000,
    bomsAffected: 5,
    linesAffected: 12,
    weeksOfCoverage: 2,
  },
  createdAt: '2026-05-08T09:14:00Z',
  dueDate: '2026-08-12',
  hasBuyerWork: true,
  hasEngWork: true,
};

const ALTERNATIVES: Alternative[] = [
  {
    crossRefId: 'xref-127',
    candidateMpn: 'AAA-127',
    candidateName: 'Equivalent ARM Cortex-M4 MCU',
    manufacturer: 'NXP Semiconductors',
    source: 'SYSTEM_AUTO',
    reviewState: 'ENG_APPROVED',
    fffMatchScore: 94,
    costDeltaPct: -3.5,
    bestPriceUsd: 7.67,
    bestSupplier: 'Avnet',
    stockUnits: 12000,
    leadTimeWeeks: 2,
    buyerRationale: 'Dual-source qualified, cost favorable, strong stock position.',
    engRationale: 'FFF approved — pin-compatible LQFP100, electrical superset, regulatory match.',
    reviewedAt: '2026-05-09',
  },
  {
    crossRefId: 'xref-124',
    candidateMpn: 'AAA-124',
    candidateName: 'Compatible STM32-class MCU',
    manufacturer: 'GigaDevice',
    source: 'MI_AGENT',
    reviewState: 'ENG_REVIEWING',
    fffMatchScore: 89,
    costDeltaPct: 1.2,
    bestPriceUsd: 8.04,
    bestSupplier: 'Arrow',
    stockUnits: 8200,
    leadTimeWeeks: 4,
    buyerRationale: 'Good supply but slightly higher cost; mfr qualified for non-critical use.',
    reviewedAt: '2026-05-09',
  },
  {
    crossRefId: 'xref-417',
    candidateMpn: 'STM32F417VGT6',
    candidateName: 'STM32F417 superset MCU',
    manufacturer: 'STMicroelectronics',
    source: 'MFR_SUGGESTED',
    reviewState: 'BUYER_REVIEWING',
    fffMatchScore: 96,
    costDeltaPct: 11.3,
    bestPriceUsd: 8.85,
    bestSupplier: 'Arrow',
    stockUnits: 4200,
    leadTimeWeeks: 6,
    reviewedAt: '2026-05-08',
  },
  {
    crossRefId: 'xref-125',
    candidateMpn: 'AAA-125',
    candidateName: 'Alt MCU — single source',
    manufacturer: 'WCH',
    source: 'BUYER_MANUAL',
    reviewState: 'DISCOVERED',
    fffMatchScore: 78,
    costDeltaPct: -8.0,
    bestPriceUsd: 7.31,
    bestSupplier: 'Direct',
    stockUnits: 25000,
    leadTimeWeeks: 1,
  },
  {
    crossRefId: 'xref-126',
    candidateMpn: 'AAA-126',
    candidateName: 'Older-gen MCU',
    manufacturer: 'Renesas',
    source: 'MI_AGENT',
    reviewState: 'BUYER_REJECTED',
    fffMatchScore: 72,
    costDeltaPct: 18.5,
    bestPriceUsd: 9.42,
    bestSupplier: 'Mouser',
    stockUnits: 0,
    leadTimeWeeks: 22,
    buyerRationale: 'No qualified distributor with stock; lead time prohibitive; cost too high.',
    reviewedAt: '2026-05-08',
  },
];

const BOMS: BomImpact[] = [
  { bomId: 'b-widget-a', bomName: 'BOM-Widget-A', unitsPerQtr: 12000, stockWeeks: 3, bestAvailableAlt: 'AAA-127' },
  { bomId: 'b-widget-b', bomName: 'BOM-Widget-B', unitsPerQtr: 3000, stockWeeks: 8, bestAvailableAlt: 'AAA-127' },
  { bomId: 'b-gizmo-c', bomName: 'BOM-Gizmo-C', unitsPerQtr: 800, stockWeeks: 1 },
  { bomId: 'b-sensor-d', bomName: 'BOM-Sensor-D', unitsPerQtr: 4500, stockWeeks: 5, bestAvailableAlt: 'AAA-127' },
  { bomId: 'b-iot-e', bomName: 'BOM-IoT-Hub-E', unitsPerQtr: 2200, stockWeeks: 2, bestAvailableAlt: 'AAA-127' },
];

// ── Visual config ────────────────────────────────────────────

const REVIEW_STATE_CONFIG: Record<ReviewState, { label: string; color: string; bg: string; icon: 'check' | 'reject' | 'pending' | 'info' }> = {
  DISCOVERED:        { label: 'Discovered',         color: '#0e7490', bg: '#cffafe', icon: 'info' },
  BUYER_REVIEWING:   { label: 'Buyer reviewing',    color: '#b45309', bg: '#fef3c7', icon: 'pending' },
  BUYER_APPROVED:    { label: 'Buyer ✓ approved',   color: '#0f766e', bg: '#ccfbf1', icon: 'check' },
  BUYER_REJECTED:    { label: 'Buyer ✗ rejected',   color: '#7f1d1d', bg: '#fee2e2', icon: 'reject' },
  ENG_REVIEWING:     { label: 'Eng FFF reviewing',  color: '#5b21b6', bg: '#ede9fe', icon: 'pending' },
  ENG_APPROVED:      { label: 'Eng ✓ approved',     color: '#15803d', bg: '#d1fae5', icon: 'check' },
  ENG_REJECTED:      { label: 'Eng ✗ rejected',     color: '#7f1d1d', bg: '#fee2e2', icon: 'reject' },
  CONDITIONAL:       { label: 'Conditional ✓',      color: '#a16207', bg: '#fef9c3', icon: 'check' },
};

const SOURCE_LABEL: Record<Alternative['source'], string> = {
  SYSTEM_AUTO:    'System auto',
  MI_AGENT:       'MI agent',
  MFR_SUGGESTED:  'Mfr suggested',
  BUYER_MANUAL:   'Buyer added',
  ENG_MANUAL:     'Eng added',
};

const SEV_COLOR: Record<string, string> = {
  CRITICAL: '#dc2626',
  HIGH: '#ea580c',
  MEDIUM: '#ca8a04',
  LOW: '#64748b',
};

// ── Action computation per role + state ──────────────────────

interface ActionSpec {
  id: string;
  label: string;
  variant: 'contained' | 'outlined' | 'text';
  color?: 'primary' | 'success' | 'error' | 'inherit';
}

function actionsForRole(alt: Alternative, role: Role): ActionSpec[] {
  if (role === 'BUYER') {
    if (alt.reviewState === 'DISCOVERED' || alt.reviewState === 'BUYER_REVIEWING') {
      return [
        { id: 'BUYER_APPROVE',  label: 'Approve & send to Eng', variant: 'contained', color: 'success' },
        { id: 'BUYER_REJECT',   label: 'Reject',                variant: 'outlined',  color: 'error' },
        { id: 'BUYER_PARK',     label: 'Park',                  variant: 'text' },
      ];
    }
    if (alt.reviewState === 'BUYER_APPROVED' || alt.reviewState === 'ENG_REVIEWING') {
      return [{ id: 'OPEN_COMPARE', label: 'Open compare', variant: 'outlined' }];
    }
    return []; // terminal states
  }
  // ENGINEER
  if (alt.reviewState === 'BUYER_APPROVED' || alt.reviewState === 'ENG_REVIEWING') {
    return [
      { id: 'ENG_APPROVE',          label: 'Approve FFF',       variant: 'contained', color: 'success' },
      { id: 'ENG_APPROVE_COND',     label: 'Conditional',       variant: 'outlined' },
      { id: 'ENG_REJECT',           label: 'Reject',            variant: 'outlined',  color: 'error' },
      { id: 'ENG_SEND_BACK',        label: 'Send back to Buyer',variant: 'text' },
    ];
  }
  if (alt.reviewState === 'DISCOVERED' || alt.reviewState === 'BUYER_REVIEWING') {
    return [{ id: 'OPEN_COMPARE', label: 'Open compare (read-only)', variant: 'outlined' }];
  }
  return []; // ENG_APPROVED / ENG_REJECTED / etc
}

// ── Main component ───────────────────────────────────────────

const OrchestratorWorkItemDetailMockPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialRole: Role = searchParams.get('role') === 'engineer' ? 'ENGINEER' : 'BUYER';

  const [role, setRole] = useState<Role>(initialRole);
  const [drawerAlt, setDrawerAlt] = useState<Alternative | null>(null);

  const cameFromBuyer = searchParams.get('role') === 'buyer';

  const counts = useMemo(() => ({
    discovered: ALTERNATIVES.filter(a => a.reviewState === 'DISCOVERED').length,
    buyerWork:  ALTERNATIVES.filter(a => ['DISCOVERED','BUYER_REVIEWING'].includes(a.reviewState)).length,
    engWork:    ALTERNATIVES.filter(a => ['BUYER_APPROVED','ENG_REVIEWING'].includes(a.reviewState)).length,
    approved:   ALTERNATIVES.filter(a => a.reviewState === 'ENG_APPROVED').length,
  }), []);

  const sevColor = SEV_COLOR[WI.severity];

  return (
    <Box sx={{ width: '100%', pb: 4 }}>
      {/* Preview banner */}
      <Box
        sx={{
          mb: 2, px: 2, py: 1, borderRadius: 1.5,
          bgcolor: alpha(theme.palette.info.main, 0.08),
          border: '1px dashed', borderColor: alpha(theme.palette.info.main, 0.4),
          display: 'flex', alignItems: 'center', gap: 1,
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 600, color: theme.palette.info.dark }}>
          DESIGN MOCK
        </Typography>
        <Typography variant="caption" color="text.secondary">
          OrchestratorWorkItem detail · per-part case · alternatives drill-down · cross-ref state · hardcoded data
        </Typography>
        <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
          <Button
            size="small"
            startIcon={<BackIcon />}
            onClick={() => navigate(cameFromBuyer ? '/buyer/worklist-mock' : '/engineering/inbox-mock')}
            sx={{ textTransform: 'none' }}
          >
            Back to {cameFromBuyer ? 'Buyer' : 'Engineering'} Worklist
          </Button>
        </Box>
      </Box>

      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            <Box component="span" onClick={() => navigate('/engineering/inbox-mock')} sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main' } }}>Worklist</Box>
            {' / '}
            <Box component="span" sx={{ color: 'text.primary', fontWeight: 500 }}>{PART.mpn}</Box>
            {' / '}
            <Box component="span">{PCN.reference}</Box>
          </Typography>
        </Stack>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>{PART.mpn}</Typography>
              <Chip label="PCN" size="small" sx={{ height: 22, bgcolor: '#fef3c7', color: '#b45309', fontWeight: 600, '& .MuiChip-label': { px: 1 } }} />
              <Chip label="EOL" size="small" sx={{ height: 22, bgcolor: '#fee2e2', color: '#b91c1c', fontWeight: 600, '& .MuiChip-label': { px: 1 } }} />
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <DotIcon sx={{ fontSize: 12, color: sevColor }} />
                <Typography variant="caption" sx={{ color: sevColor, fontWeight: 600, textTransform: 'capitalize' }}>{WI.severity.toLowerCase()}</Typography>
              </Stack>
              <Chip label={WI.status} size="small" variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>{PART.description}</Typography>
            <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary"><b>${(WI.exposure.revenueAtRisk! / 1_000_000).toFixed(1)}M</b> at risk</Typography>
              <Typography variant="caption" color="text.secondary"><b>{WI.exposure.bomsAffected}</b> BOMs · <b>{WI.exposure.linesAffected}</b> lines</Typography>
              <Typography variant="caption" color="text.secondary">LTB: <b>{PCN.ltbDate}</b></Typography>
              <Typography variant="caption" color="text.secondary">Coverage: <b>{WI.exposure.weeksOfCoverage}w</b></Typography>
            </Stack>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button size="small" variant="outlined" sx={{ textTransform: 'none' }}>Snooze</Button>
            <Button size="small" variant="outlined" sx={{ textTransform: 'none' }}>Assign</Button>
            <Button size="small" variant="contained" sx={{ textTransform: 'none', boxShadow: 'none' }}>Resolve</Button>
          </Stack>
        </Box>

        {/* Role toggle (mock-only — demonstrates dual visibility) */}
        <Box sx={{ mt: 2, p: 1.25, borderRadius: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.04), border: '1px dashed', borderColor: alpha(theme.palette.primary.main, 0.3), display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Viewing as</Typography>
          <ToggleButtonGroup
            value={role} exclusive size="small"
            onChange={(_, v) => { if (v) setRole(v); }}
            sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontSize: '0.75rem', py: 0.25, px: 1.5 } }}
          >
            <ToggleButton value="BUYER">Buyer ({counts.buyerWork} to do)</ToggleButton>
            <ToggleButton value="ENGINEER">Engineer ({counts.engWork} to do)</ToggleButton>
          </ToggleButtonGroup>
          <Typography variant="caption" color="text.disabled" sx={{ ml: 'auto' }}>
            (mock-only toggle — in production, route determines role)
          </Typography>
        </Box>
      </Box>

      {/* Trigger context + activity rail */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 2, mb: 3 }}>
        <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: theme.palette.divider, borderRadius: 2 }}>
          <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600 }}>Trigger context</Typography>
          <Box sx={{ mt: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <AttachIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{PCN.reference}</Typography>
              <Chip label={`${PCN.detectionSource} · conf ${PCN.detectionConfidence}`} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
              <Box sx={{ ml: 'auto' }}>
                <Button size="small" sx={{ textTransform: 'none' }}>Open original</Button>
              </Box>
            </Stack>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
              <Typography variant="caption" color="text.secondary">Manufacturer: <b>{PCN.manufacturer}</b></Typography>
              <Typography variant="caption" color="text.secondary">Change type: <b>{PCN.changeType}</b></Typography>
              <Typography variant="caption" color="text.secondary">Effective: <b>{PCN.effectiveDate}</b></Typography>
              <Typography variant="caption" color="text.secondary">LTB: <b>{PCN.ltbDate}</b></Typography>
              <Typography variant="caption" color="text.secondary">Last ship: <b>{PCN.lastShipDate}</b></Typography>
              <Typography variant="caption" color="text.secondary">Mfr suggested alt: <b>{PCN.mfrSuggestedAlt}</b></Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
              {PCN.changeDescription}
            </Typography>
          </Box>
        </Paper>

        <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: theme.palette.divider, borderRadius: 2 }}>
          <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600 }}>Activity</Typography>
          <Box sx={{ mt: 1 }}>
            {[
              { at: '2026-05-09 14:22', actor: 'Engineer', text: 'Approved AAA-127 (FFF score 94)' },
              { at: '2026-05-09 11:08', actor: 'Buyer',    text: 'Approved AAA-127 → sent to Eng' },
              { at: '2026-05-09 10:51', actor: 'Buyer',    text: 'Rejected AAA-126 (no supplier)' },
              { at: '2026-05-08 16:04', actor: 'System',   text: 'MI agent surfaced 3 candidates' },
              { at: '2026-05-08 09:14', actor: 'System',   text: 'WI created from PCN-ST-2026-0142' },
            ].map((e, i) => (
              <Box key={i} sx={{ py: 0.5, borderLeft: '2px solid', borderColor: alpha(theme.palette.primary.main, 0.2), pl: 1, mb: 0.5 }}>
                <Typography variant="caption" sx={{ display: 'block', fontWeight: 500 }}>{e.text}</Typography>
                <Typography variant="caption" color="text.disabled">{e.actor} · {e.at}</Typography>
              </Box>
            ))}
          </Box>
        </Paper>
      </Box>

      {/* Alternatives (the heart of the body) */}
      <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: theme.palette.divider, borderRadius: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600 }}>Alternatives</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {ALTERNATIVES.length} candidates · {counts.approved} engineering-approved · {role === 'BUYER' ? counts.buyerWork : counts.engWork} need your review
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button size="small" startIcon={<AddIcon />} variant="outlined" sx={{ textTransform: 'none' }}>Add manually</Button>
            <Button size="small" startIcon={<AgentIcon />} variant="outlined" sx={{ textTransform: 'none' }}>Run agent search</Button>
          </Stack>
        </Box>

        {/* Header row */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: '1.4fr 1fr 0.8fr 0.7fr 0.9fr 0.8fr 1.6fr',
          gap: 1, py: 1, px: 1,
          fontSize: '0.7rem', fontWeight: 600, color: 'text.secondary',
          textTransform: 'uppercase', letterSpacing: '0.04em',
        }}>
          <Box>Candidate</Box>
          <Box>State</Box>
          <Box>FFF score</Box>
          <Box>Cost Δ</Box>
          <Box>Best price</Box>
          <Box>Stock / LT</Box>
          <Box>Actions</Box>
        </Box>

        {ALTERNATIVES.map((alt) => {
          const stateCfg = REVIEW_STATE_CONFIG[alt.reviewState];
          const acts = actionsForRole(alt, role);
          const isTerminal = alt.reviewState === 'BUYER_REJECTED' || alt.reviewState === 'ENG_REJECTED' || alt.reviewState === 'ENG_APPROVED';
          return (
            <Box
              key={alt.crossRefId}
              sx={{
                display: 'grid',
                gridTemplateColumns: '1.4fr 1fr 0.8fr 0.7fr 0.9fr 0.8fr 1.6fr',
                gap: 1, alignItems: 'center', py: 1.25, px: 1,
                borderTop: '1px solid', borderColor: theme.palette.divider,
                opacity: isTerminal && alt.reviewState !== 'ENG_APPROVED' ? 0.6 : 1,
                cursor: 'pointer',
                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.03) },
              }}
              onClick={() => setDrawerAlt(alt)}
            >
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>{alt.candidateMpn}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {alt.manufacturer} · {SOURCE_LABEL[alt.source]}
                </Typography>
              </Box>
              <Box>
                <Chip
                  label={stateCfg.label}
                  size="small"
                  sx={{
                    height: 22, fontSize: '0.7rem', fontWeight: 600,
                    bgcolor: stateCfg.bg, color: stateCfg.color,
                    '& .MuiChip-label': { px: 0.75 },
                  }}
                />
              </Box>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, color: alt.fffMatchScore >= 90 ? '#15803d' : alt.fffMatchScore >= 80 ? '#ca8a04' : '#dc2626' }}>
                  {alt.fffMatchScore}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500, color: alt.costDeltaPct < 0 ? '#15803d' : alt.costDeltaPct > 5 ? '#dc2626' : 'text.primary' }}>
                  {alt.costDeltaPct > 0 ? '+' : ''}{alt.costDeltaPct.toFixed(1)}%
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ display: 'block', fontWeight: 500 }}>${alt.bestPriceUsd.toFixed(2)}</Typography>
                <Typography variant="caption" color="text.disabled">{alt.bestSupplier}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ display: 'block', color: alt.stockUnits === 0 ? '#dc2626' : 'text.primary' }}>
                  {alt.stockUnits.toLocaleString()} u
                </Typography>
                <Typography variant="caption" color="text.disabled">{alt.leadTimeWeeks}w LT</Typography>
              </Box>
              <Box onClick={(e) => e.stopPropagation()}>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {acts.map((a) => (
                    <Button key={a.id} size="small" variant={a.variant} color={a.color || 'primary'} sx={{ textTransform: 'none', fontSize: '0.7rem', py: 0.25, px: 1, minWidth: 0 }}>
                      {a.label}
                    </Button>
                  ))}
                  {acts.length === 0 && <Typography variant="caption" color="text.disabled">—</Typography>}
                </Stack>
              </Box>
            </Box>
          );
        })}
      </Paper>

      {/* Per-BOM decisions section */}
      <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: theme.palette.divider, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600 }}>Per-BOM decisions (Phase 2)</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {role === 'BUYER'
                ? 'Read-only view — engineering decides per-BOM after FFF approvals'
                : 'Pick a path per BOM. SWAP requires an ENG-approved alternate.'}
            </Typography>
          </Box>
          {role === 'ENGINEER' && (
            <Button variant="contained" size="small" sx={{ textTransform: 'none', boxShadow: 'none' }}>
              Apply 5 decisions
            </Button>
          )}
        </Box>

        <Box sx={{
          display: 'grid',
          gridTemplateColumns: '1.5fr 0.8fr 0.7fr 1.5fr 1.5fr',
          gap: 1, py: 1, px: 1,
          fontSize: '0.7rem', fontWeight: 600, color: 'text.secondary',
          textTransform: 'uppercase', letterSpacing: '0.04em',
        }}>
          <Box>BOM</Box>
          <Box>Units / qtr</Box>
          <Box>Stock</Box>
          <Box>Best alt available</Box>
          <Box>Decision</Box>
        </Box>

        {BOMS.map((b) => {
          const altInfo = b.bestAvailableAlt ? ALTERNATIVES.find(a => a.candidateMpn === b.bestAvailableAlt) : null;
          const altReady = altInfo?.reviewState === 'ENG_APPROVED' || altInfo?.reviewState === 'CONDITIONAL';
          return (
            <Box key={b.bomId} sx={{
              display: 'grid',
              gridTemplateColumns: '1.5fr 0.8fr 0.7fr 1.5fr 1.5fr',
              gap: 1, alignItems: 'center', py: 1, px: 1,
              borderTop: '1px solid', borderColor: theme.palette.divider,
            }}>
              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.85rem' }}>{b.bomName}</Typography>
              <Typography variant="caption">{b.unitsPerQtr.toLocaleString()}</Typography>
              <Typography variant="caption" color={b.stockWeeks <= 4 ? '#dc2626' : 'text.primary'} sx={{ fontWeight: b.stockWeeks <= 4 ? 600 : 400 }}>
                {b.stockWeeks}w
              </Typography>
              <Typography variant="caption">
                {b.bestAvailableAlt ? (
                  <span style={{ color: altReady ? '#15803d' : '#ca8a04', fontWeight: 600 }}>
                    {altReady ? '✓ ' : '⏳ '}{b.bestAvailableAlt}
                  </span>
                ) : <span style={{ color: '#64748b' }}>(none yet)</span>}
              </Typography>
              <Box>
                {role === 'ENGINEER' ? (
                  <TextField
                    select
                    size="small"
                    SelectProps={{ native: true }}
                    defaultValue={altReady ? 'SWAP' : ''}
                    sx={{ minWidth: 180, '& select': { fontSize: '0.8rem', py: 0.5 } }}
                  >
                    <option value="">Choose...</option>
                    <option value="SWAP" disabled={!altReady}>Swap to {b.bestAvailableAlt || '...'}</option>
                    <option value="LTB_STOCKPILE">LTB stockpile</option>
                    <option value="NEGOTIATE_LTB">Negotiate LTB (→ buyer)</option>
                    <option value="REDESIGN">Redesign</option>
                    <option value="NO_OP">No action</option>
                  </TextField>
                ) : (
                  <Typography variant="caption" color="text.disabled">{altReady ? `Pending engineer choice (suggested: Swap to ${b.bestAvailableAlt})` : 'Waiting on alternates'}</Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Paper>

      {/* Drawer: deep compare for one alt */}
      <Drawer anchor="right" open={!!drawerAlt} onClose={() => setDrawerAlt(null)} PaperProps={{ sx: { width: 720 } }}>
        {drawerAlt && (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600 }}>Deep compare</Typography>
              <Box sx={{ ml: 'auto' }}>
                <Button size="small" onClick={() => setDrawerAlt(null)} sx={{ textTransform: 'none' }}>Close</Button>
              </Box>
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>{PART.mpn} → {drawerAlt.candidateMpn}</Typography>
            <Typography variant="caption" color="text.secondary">{drawerAlt.manufacturer} · {SOURCE_LABEL[drawerAlt.source]}</Typography>

            <Box sx={{ mt: 2, mb: 2 }}>
              <Chip label={REVIEW_STATE_CONFIG[drawerAlt.reviewState].label} size="small" sx={{
                height: 24, fontWeight: 600,
                bgcolor: REVIEW_STATE_CONFIG[drawerAlt.reviewState].bg,
                color: REVIEW_STATE_CONFIG[drawerAlt.reviewState].color,
              }} />
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* FFF scores */}
            <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600 }}>FFF analysis</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1, mt: 1, mb: 2 }}>
              {[
                { label: 'Form',     score: Math.min(100, drawerAlt.fffMatchScore + 2) },
                { label: 'Fit',      score: Math.max(60, drawerAlt.fffMatchScore - 4) },
                { label: 'Function', score: Math.min(100, drawerAlt.fffMatchScore + 1) },
              ].map((s) => (
                <Box key={s.label} sx={{ p: 1.5, borderRadius: 1, bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
                  <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: s.score >= 90 ? '#15803d' : s.score >= 80 ? '#ca8a04' : '#dc2626' }}>{s.score}</Typography>
                </Box>
              ))}
            </Box>

            {/* Spec compare placeholder */}
            <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600 }}>Side-by-side specs</Typography>
            <Box sx={{ mt: 1, mb: 2, p: 1.5, borderRadius: 1, border: '1px solid', borderColor: theme.palette.divider }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1, fontSize: '0.8rem' }}>
                <Box sx={{ fontWeight: 600 }}>Attribute</Box>
                <Box sx={{ fontWeight: 600 }}>{PART.mpn}</Box>
                <Box sx={{ fontWeight: 600 }}>{drawerAlt.candidateMpn}</Box>
                {[
                  ['Package',       'LQFP100',      'LQFP100'],
                  ['Pinout',        'Standard',     'Compatible'],
                  ['Core',          'ARM-CM4',      'ARM-CM4'],
                  ['Clock max',     '168 MHz',      '180 MHz'],
                  ['Flash',         '1024 KB',      '1024 KB'],
                  ['SRAM',          '192 KB',       '192 KB'],
                  ['Vcc range',     '1.8–3.6 V',    '1.8–3.6 V'],
                  ['Temp range',    '-40 / +85 °C', '-40 / +85 °C'],
                  ['RoHS / REACH',  '✓ / ✓',        '✓ / ✓'],
                ].map((row) => (
                  <React.Fragment key={row[0]}>
                    <Box sx={{ color: 'text.secondary' }}>{row[0]}</Box>
                    <Box>{row[1]}</Box>
                    <Box sx={{ color: row[1] === row[2] ? 'inherit' : '#0e7490', fontWeight: row[1] === row[2] ? 400 : 500 }}>{row[2]}</Box>
                  </React.Fragment>
                ))}
              </Box>
            </Box>

            {/* Buyer + Eng review summaries */}
            {drawerAlt.buyerRationale && (
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600 }}>Buyer review</Typography>
                <Typography variant="body2" sx={{ fontSize: '0.85rem', mt: 0.5 }}>{drawerAlt.buyerRationale}</Typography>
              </Box>
            )}
            {drawerAlt.engRationale && (
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600 }}>Engineer review</Typography>
                <Typography variant="body2" sx={{ fontSize: '0.85rem', mt: 0.5 }}>{drawerAlt.engRationale}</Typography>
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Action area */}
            <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600 }}>Decision</Typography>
            <TextField
              fullWidth
              multiline
              rows={2}
              size="small"
              placeholder="Add a note (optional, will be saved to review history)…"
              sx={{ mt: 1, mb: 2, '& .MuiInputBase-input': { fontSize: '0.85rem' } }}
            />
            <Stack direction="row" spacing={1}>
              {actionsForRole(drawerAlt, role).map((a) => (
                <Button key={a.id} variant={a.variant} color={a.color || 'primary'} sx={{ textTransform: 'none', boxShadow: 'none' }}>
                  {a.label}
                </Button>
              ))}
            </Stack>
            {actionsForRole(drawerAlt, role).length === 0 && (
              <Typography variant="caption" color="text.disabled">No actions available — alternative is in a terminal state for your role.</Typography>
            )}
          </Box>
        )}
      </Drawer>

      {/* Footnote */}
      <Box sx={{ mt: 2, color: 'text.disabled' }}>
        <Typography variant="caption">
          What this mock demonstrates: cross-ref state lives on PartCrossReference (DISCOVERED → BUYER_REVIEWING → BUYER_APPROVED → ENG_REVIEWING → ENG_APPROVED).
          Both roles see the same case; available actions per row depend on the alt's current state and the viewer's role.
          Phase 2 (per-BOM decisions) gates on alts being ENG_APPROVED.
        </Typography>
      </Box>
    </Box>
  );
};

export default OrchestratorWorkItemDetailMockPage;
